import { StorageKeys } from 'common/storage/constants';
import { urlDateFormat } from 'common/UrlDate';
import { isNil, last } from 'lodash';
import { DateTime } from 'luxon';
import React, { useMemo, useRef, useState } from 'react';
import { AddOnItemType, FoodType, useSuggestedSidesQuery } from 'src/gqlReactTypings.generated.d';
import { MAIN_FOOD_TYPES, SIDE_FOOD_TYPES } from 'src/shared/Constants';
import { ChevronLeft, ChevronRight } from 'src/shared/design-system/Icon';
import { useLocalStorage } from 'src/shared/storage/Storage';
import { ifProp } from 'src/shared/styled-utils/ifProp';
import { useDispatch, useSelector } from 'src/store';
import { incLineItemQuantity, setAddOnItemQuantity } from 'src/store/cart/actions';
import { selectAddOnItems, selectAvailableAddOnItemsByType, selectCart } from 'src/store/cart/selectors';
import { CartFoodItem, ServerAddOnItem, WholeCartLineItem } from 'src/store/cart/types';
import { useCachedZipCode } from 'src/user-preferences/useCachedZipCode';
import styled from 'styled-components';
import { useScreenContext } from 'web-common/src/shared/context/ScreenContext/hook';
import { DietaryTags } from 'web-common/src/shared/Enums';
import { Colors, FontFamily, FontSize, FontWeight, LineHeight } from 'web-common/src/shared/styles';
import { SideCard } from './SideCard';

const InlineWrapper = styled.div`
  margin-top: 24px;
`;

const HeaderRow = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
`;
const Header = styled.span`
  font-family: ${FontFamily.SANS};
  font-weight: ${FontWeight.SEMI_BOLD};
  font-size: ${FontSize.S};
  line-height: ${LineHeight.XS};
  color: ${Colors.GRAY_DARK_30};
`;

const ScrollButtonContainer = styled.div`
  display: flex;
  flex-direction: row;
  height: fit-content;
  gap: 12px;
`;

const ScrollButtonLeft = styled(ChevronLeft)<{ disabled: boolean }>`
  color: ${ifProp('disabled', Colors.GRAY_LITE_20, Colors.GRAY)};
`;

const ScrollButtonRight = styled(ChevronRight)<{ disabled: boolean }>`
  color: ${ifProp('disabled', Colors.GRAY_LITE_20, Colors.GRAY)};
`;

const CardTrack = styled.div`
  display: flex;
  overflow-x: scroll;

  // Ensure that the negative margin within suggested sides matches the outside padding
  margin-left: calc(var(--container-padding, 16px) * -1);
  margin-right: calc(var(--container-padding, 16px) * -1);
  padding-bottom: 10px;
`;

const getFoodItemIdsForFoodTypes = ({
  foodTypes,
  lineItems,
}: {
  foodTypes: (FoodType | null | undefined)[];
  lineItems: readonly WholeCartLineItem[];
}) => lineItems.filter((item) => foodTypes.includes(item.foodItem.foodType)).map((item) => item.foodItemId);

interface SuggestedSidesProps {
  shefLineItems: readonly WholeCartLineItem[];
  show?: boolean;
  deliveryDate: DateTime;
  shefTimeZone: string;
  isEditingOrder: boolean;
}

const SCROLL_BY = 185;

export const SuggestedSides: React.FC<SuggestedSidesProps> = ({
  shefLineItems,
  shefTimeZone,
  deliveryDate,
  isEditingOrder,
}) => {
  const dispatch = useDispatch();
  const deliveryUrlDate = deliveryDate.toFormat(urlDateFormat);

  const incrementQuantity = (foodItem: CartFoodItem) => {
    dispatch(incLineItemQuantity(isEditingOrder, { foodItem, deliveryDate: deliveryUrlDate, location: 'cart' }));
  };

  const setAddOnQuantity = (addOnItem: ServerAddOnItem, quantity: number) => {
    dispatch(setAddOnItemQuantity(isEditingOrder, { addOnItem, quantity }));
  };

  const { availableSideAddOnItems, addOnItems } = useSelector((state) => {
    const cart = selectCart(state, isEditingOrder);
    return {
      addOnItems: selectAddOnItems(cart),
      availableSideAddOnItems: selectAvailableAddOnItemsByType(cart, AddOnItemType.Side),
    };
  });

  const cartMainFoodItemIds = getFoodItemIdsForFoodTypes({ foodTypes: MAIN_FOOD_TYPES, lineItems: shefLineItems });
  const cartSideFoodItemIds = getFoodItemIdsForFoodTypes({ foodTypes: SIDE_FOOD_TYPES, lineItems: shefLineItems });

  const [zipCode] = useCachedZipCode();
  const [cachedDietaryPreferences] = useLocalStorage<DietaryTags[]>(StorageKeys.DIETARY_PREFERENCES, []);

  const { data } = useSuggestedSidesQuery({
    variables: {
      foodItemId: last(shefLineItems)?.foodItemId ?? 0,
      numMains: cartMainFoodItemIds.length,
      deliveryDate: deliveryDate.toISO(),
      shefTimeZone,
      dietaryFilters: cachedDietaryPreferences,
      zipCode,
    },
  });

  const suggestedSides = useMemo(() => {
    if (!data?.suggestedItemsFromMenu) {
      return [];
    }

    return data.suggestedItemsFromMenu.filter((side) => !cartSideFoodItemIds.includes(side.foodItemId));
  }, [cartSideFoodItemIds, data?.suggestedItemsFromMenu]);

  const scrollContainer = useRef<HTMLDivElement>(null);
  const [scrollDistance, setScrollDistance] = useState(0);
  const canScrollLeft = scrollDistance > 0;
  const canScrollRight =
    scrollDistance <= (scrollContainer.current?.scrollWidth || 0) - (scrollContainer.current?.clientWidth || 0);
  const scroll = (distance: number) => {
    const node = scrollContainer.current;
    if (node) {
      const newScrollDistance = scrollDistance + distance;
      node.scroll({ left: newScrollDistance, behavior: 'smooth' });
      setScrollDistance(newScrollDistance);
    }
  };
  const scrollLeft = () => {
    if (canScrollLeft) {
      scroll(-1 * SCROLL_BY);
    }
  };
  const scrollRight = () => {
    if (canScrollRight) {
      scroll(SCROLL_BY);
    }
  };

  const { isSmallScreen } = useScreenContext();

  const renderableAddons = availableSideAddOnItems.filter((item) =>
    isNil(addOnItems.find((addon) => addon.id === item.id))
  );

  if (!suggestedSides.length && !renderableAddons.length) {
    return null;
  }

  return (
    <InlineWrapper>
      <HeaderRow>
        <Header>People Also Ordered</Header>

        <ScrollButtonContainer>
          <ScrollButtonLeft onClick={scrollLeft} disabled={!canScrollLeft} width={20} height={20} />
          <ScrollButtonRight onClick={scrollRight} disabled={!canScrollRight} width={20} height={20} />
        </ScrollButtonContainer>
      </HeaderRow>
      <CardTrack ref={scrollContainer}>
        {renderableAddons.map((item) => (
          <SideCard
            key={`addon-${item.id}`}
            imageUrl={item.imageUrl}
            description={item.displayText}
            name={item.name}
            price={item.price}
            incrementQuantity={() => setAddOnQuantity(item, 1)}
            smallScreen={isSmallScreen}
          />
        ))}
        {suggestedSides.map((side) => (
          <SideCard
            key={`food-${side.foodItemId}`}
            imageUrl={side.foodItem.imageUrl}
            description={side.foodItem.description}
            name={side.foodItem.name}
            price={side.foodItem.price}
            type={side.foodItem.foodType}
            incrementQuantity={() => incrementQuantity(side.foodItem)}
            smallScreen={isSmallScreen}
          />
        ))}
      </CardTrack>
    </InlineWrapper>
  );
};
